import React, { useCallback, useEffect, useState, useMemo } from "react";
import { viewSinglePaymentTransaction } from "../../services/transaction";
import toast from "react-hot-toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Toggle from "../../components/Toggle";
import { useAuth } from "../../context/AuthContextProvider";
import LoadingScreen from "../../components/LoadingScreen";
import NoTransactions from "./NoPaymentTransaction";
import { useParams } from "react-router-dom";
import PaymentModal from "./PaymentModal";

const PaymentLinkTransactions = () => {
    const [transactions, setTransactions] = useState([]);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [transactInfo, setTransactInfo] = useState({});

    const { id } = useParams();
    const { globalProfile } = useAuth();

    const fetchTransactions = useCallback(async () => {
        setLoading(true);
        try {
            const res = await viewSinglePaymentTransaction(id);
            setTransactions(res.data || []);
            setLoading(false);
        } catch (error) {
            if (error.response) {
                toast.error(error.response.data.message);
            }
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions, globalProfile]);

    const filteredTransactions = useMemo(() => {
        if (!status) return transactions;
        return transactions.filter(
            (t) => t.Status?.toLowerCase() === status.toLowerCase()
        );
    }, [transactions, status]);

    const handleProfile = (info) => {
        setModalOpen(true);
        setTransactInfo(info);
    };

    if (loading) {
        return <LoadingScreen title="Transactions" />;
    }

    if (transactions.length === 0) {
        return <NoTransactions />;
    }

    return (
        <div className="">
            <div className="flex flex-col xl:flex-row items-start justify-between">
                <h1 className="text-navy-blue text-2xl font-bold">Payment Link Transactions</h1>

                <div className="flex sm:flex-row flex-col justify-center items-center">
                    <Toggle />
                </div>
            </div>

            <div className="bg-white mr-5 md:mr-0 sm:mt-4 sm:ml-3 sm:px-7 sm:py-5">
                <div className="flex items-center sm:justify-between mb-4">
                    <div className="flex flex-col-reverse sm:flex-row sm:items-center">
                        <p className="border-r pr-4 text-sm font-bold text-black-shade">
                            Transactions
                        </p>

                        <div className="flex sm:flex-row items-center border-r px-4">
                            <div className="flex items-center gap-1 mr-2">
                                <div className="rounded-full bg-green-400 w-[6px] h-[6px]"></div>
                                <p
                                    className={`text-[12px] font-normal cursor-pointer ${
                                        status === "APPROVED" ? "font-bold text-green-600" : "text-black-shade"
                                    }`}
                                    onClick={() => setStatus((prev) => (prev === "APPROVED" ? null : "APPROVED"))}
                                >
                                    Approved
                                </p>
                            </div>
                            <div className="flex items-center gap-1 mr-2">
                                <div className="rounded-full bg-red-400 w-[6px] h-[6px]"></div>
                                <p
                                    className={`text-[12px] font-normal cursor-pointer ${
                                        status === "Declined" ? "font-bold text-red-600" : "text-black-shade"
                                    }`}
                                    onClick={() => setStatus((prev) => (prev === "Declined" ? null : "Declined"))}
                                >
                                    Declined
                                </p>
                            </div>
                            <div className="flex items-center gap-1">
                                <div className="rounded-full bg-black-shade w-[6px] h-[6px]"></div>
                                <p
                                    className={`text-[12px] font-normal cursor-pointer ${
                                        status === "Initiated" ? "font-bold text-blue-600" : "text-black-shade"
                                    }`}
                                    onClick={() => setStatus((prev) => (prev === "Initiated" ? null : "Initiated"))}
                                >
                                    Initiated
                                </p>
                            </div>

                            {status && (
                                <button
                                    onClick={() => setStatus(null)}
                                    className="ml-3 text-xs text-gray-500 underline"
                                >
                                    Clear filter
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <DataTable
                    value={filteredTransactions}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 20]}
                    emptyMessage="No transactions found."
                    onRowClick={(e) => handleProfile(e.data)}
                    rowClassName={() => "cursor-pointer hover:bg-gray-50 transition-colors"}
                    className="p-datatable-sm"
                    responsiveLayout="scroll"
                >
                    <Column
                        field="Order Id"
                        header="Order ID"
                        sortable
                        body={(row) => <div className="py-2">{row["Order Id"]}</div>}
                    />
                    <Column
                        field="PAN"
                        header="Masked PAN"
                        sortable
                        body={(row) => <div className="py-2">{row.PAN}</div>}
                    />
                    <Column
                        field="Convenience Fee"
                        header="Convenience Fee"
                        sortable
                        body={(row) => <div className="py-2">{row["Convenience Fee"]}</div>}
                    />
                    <Column
                        field="Description"
                        header="Description"
                        sortable
                        body={(row) => <div className="py-2">{row.Description}</div>}
                    />
                    <Column
                        field="Status"
                        header="Status"
                        sortable
                        body={(row) => {
                            if (!row.Status) return "-";
                            const s = row.Status.toLowerCase();
                            const colorClass =
                                s === "approved"
                                    ? "text-green-500 font-medium"
                                    : s === "declined"
                                    ? "text-red-500 font-medium"
                                    : "text-gray-600";
                            return (
                                <span className={colorClass}>
                                    {row.Status.charAt(0).toUpperCase() +
                                        row.Status.slice(1).toLowerCase()}
                                </span>
                            );
                        }}
                    />
                    <Column
                        field="CustomerName"
                        header="Customer Name"
                        sortable
                        body={(row) => (
                            <div className="py-2">{row.CustomerName || "N/A"}</div>
                        )}
                    />
                    <Column
                        field="Scheme"
                        header="Scheme"
                        sortable
                        body={(row) => <div className="py-2">{row.Scheme}</div>}
                    />
                    <Column
                        field="Amount"
                        header="Amount"
                        sortable
                        body={(row) => (
                            <div className="py-2">
                                {row.Amount != null ? (typeof row.Amount === 'number' ? row.Amount.toFixed(2) : row.Amount) : "-"}
                            </div>
                        )}
                    />
                    <Column
                        field="Currency"
                        header="Currency"
                        sortable
                        body={(row) => <div className="py-2">{row.Currency}</div>}
                    />
                    <Column
                        field="TranTime"
                        header="Transaction Date"
                        sortable
                        body={(row) => <div className="py-2">{row.TranTime}</div>}
                    />
                </DataTable>
            </div>

            {modalOpen && (
                <PaymentModal
                    setIsModalOpen={setModalOpen}
                    transactData={transactInfo}
                />
            )}
        </div>
    );
};

export default PaymentLinkTransactions;
